import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { useEffect } from "react";
import { fetchUserTagData } from "@Actions/setUserTagData";
import { RootState } from "@Reducers/index";
import type { UserTagProfileData } from "@ServerTypes/accounts/accounts.schema";
import { objectIdString } from "@Helpers/schemas/common.schema";

/**
 * `userData` is `Partial<UserTagProfileData>` rather than the entry's `data` type because the
 * `|| {}` fallback below is load-bearing: the tag store has no entry for `id` until the fetch
 * dispatched by the effect resolves, so the first render always sees `{}`.
 */
function useUserCardData({ id }: { id: string }) {
  const userTagData = useSelector((state: RootState) => state.userTagData);
  const userData: Partial<UserTagProfileData> = userTagData[id]?.data || {};
  const dispatch = useDispatch();
  useEffect(() => {
    // `fetchUserTagData` declares a branded `ObjectIdString`, while this hook's `id` is the account
    // _id read out of a `users`-question answer, which is untyped on the wire. It is parsed rather
    // than asserted: an answer carrying anything but a 24-hex id could never resolve to an account,
    // so no fetch is dispatched for it. (Tightening the hook's own `id` would cascade through
    // `userCard.tsx` and the mobile `RenderAddedUserCard`, neither of which is in this pass.)
    const accountId = objectIdString.safeParse(id);
    if (accountId.success) {
      dispatch(fetchUserTagData(accountId.data));
    }
  }, [id]);
  return {
    userData,
  };
}

export default useUserCardData;
